import React from 'react';
import { SlideOver, Header } from '@lifeomic/chroma-react/components/SlideOver';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { defineMessages, useIntl } from 'react-intl';
import { Test } from './types';
import { Box } from '@lifeomic/chroma-react/components/Box';
import {
  SequenceDetailsBadge,
  SequenceDetailsBox,
  SequenceDetailsLabel,
  SequenceDetailsPair,
  SequenceDetailsValue,
} from './SequenceDetails';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Divider } from '@lifeomic/chroma-react/components/Divider';
import clsx from 'clsx';
import { lighten } from '@mui/material/styles';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import { MapPin } from '@lifeomic/chromicons';

const messages = defineMessages({
  biomarkers: {
    id: 'omicsExplorer.biomarkerSlideOver.biomarkers',
    defaultMessage: 'Biomarkers',
  },
  noBiomarkers: {
    id: 'omicsExplorer.biomarkerSlideOver.noBiomarkers',
    defaultMessage: 'No biomarkers',
  },
  tumorProfile: {
    id: 'omicsExplorer.biomarkerSlideOver.tumorProfile',
    defaultMessage: 'Tumor Profile',
  },
  testMSILabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testMSILabel',
    defaultMessage: 'MSI: ',
  },
  tooltipMSI: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipMSI',
    defaultMessage: 'Microsatellite Instability',
  },
  testTMBLabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testTMBLabel',
    defaultMessage: 'TMB: ',
  },
  tooltipLOH: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipLOH',
    defaultMessage: 'Genome-wide Loss-of-heterozygosity ',
  },
  testLOHLabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testLOHLabel',
    defaultMessage: 'gwLOH: ',
  },
  testLOHScore: {
    id: 'omicsExplorer.biomarkerSlideOver.testLOHScore',
    defaultMessage: '{lohScore}%',
  },
  tooltipTMB: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipTMB',
    defaultMessage: 'Tumor Mutation Burden',
  },
  testPTFLabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testPTFLabel',
    defaultMessage: 'PTF: ',
  },
  tooltipPTF: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipPTF',
    defaultMessage: 'Plasma Tumor Fraction',
  },
  testHRDLabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testHRDLabel',
    defaultMessage: 'HRD Status: ',
  },
  tooltipHRD: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipHRD',
    defaultMessage: 'Homologous Recombination Deficiency',
  },
  testCellPurityLabel: {
    id: 'omicsExplorer.biomarkerSlideOver.testCellPurityLabel',
    defaultMessage: 'Cell Purity: ',
  },
  tooltipCellPurity: {
    id: 'omicsExplorer.biomarkerSlideOver.tooltipCellPurity',
    defaultMessage: 'Cell Purity',
  },
  nonHumanContent: {
    id: 'omicsExplorer.biomarkerSlideOver.nonHumanContent',
    defaultMessage: 'Non-Human Content',
  },
  nhc: {
    id: 'omicsExplorer.biomarkerSlideOver.nhc',
    defaultMessage: 'Organism: {organism}, Reads: {reads}, Status: {status}',
  },
  immunohistochemistry: {
    id: 'omicsExplorer.biomarkerSlideOver.immunohistochemistry',
    defaultMessage: 'Immunohistochemistry',
  },
  ihcBiomarker: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcBiomarker',
    defaultMessage: 'Biomarker',
  },
  result: {
    id: 'omicsExplorer.biomarkerSlideOver.result',
    defaultMessage: 'Result',
  },
  ihcIntensity: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcIntensity',
    defaultMessage: 'Intensity',
  },
  ihcStainPercent: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcStainPercent',
    defaultMessage: 'Stain Percent',
  },
  ihcThreshold: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcThreshold',
    defaultMessage: 'Threshold',
  },
  ihcTcResult: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcTcResult',
    defaultMessage: 'Tumor Cell Result',
  },
  ihcTcIntensity: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcTcIntensity',
    defaultMessage: 'Tumor Cell Intensity',
  },
  ihcTcStainPercent: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcTcStainPercent',
    defaultMessage: 'Tumor Cell Stain Percent',
  },
  ihcTcThreshold: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcTcThreshold',
    defaultMessage: 'Tumor Cell Threshold',
  },
  ihcIcResult: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcIcResult',
    defaultMessage: 'Immune Cell Result',
  },
  ihcIcStainPercent: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcIcStainPercent',
    defaultMessage: 'Immune Cell Stain Percent',
  },
  ihcIcThreshold: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcIcThreshold',
    defaultMessage: 'Immune Cell Threshold',
  },
  ihcCpScore: {
    id: 'omicsExplorer.biomarkerSlideOver.ihcCpScore',
    defaultMessage: 'CP Score',
  },
  hla: {
    id: 'omicsExplorer.biomarkerSlideOver.hla',
    defaultMessage: 'Human Leukocyte Antigen',
  },
  mhcClassI: {
    id: 'omicsExplorer.biomarkerSlideOver.mhcClassI',
    defaultMessage: 'MHC Class I',
  },
  genotype: {
    id: 'omicsExplorer.biomarkerSlideOver.genotype',
    defaultMessage: 'Genotype',
  },
  gene: {
    id: 'omicsExplorer.biomarkerSlideOver.gene',
    defaultMessage: 'Gene',
  },
  hyperdiploidy: {
    id: 'omicsExplorer.biomarkerSlideOver.hyperdiploidy',
    defaultMessage: 'Hyperdiploidy',
  },
  hyperdiploidyTooltip: {
    id: 'omicsExplorer.biomarkerSlideOver.hyperdiploidyTooltip',
    defaultMessage: 'Chromosome Trisomies',
  },
});

const useStyles = makeStyles(theme => ({
  tumorDetails: {
    flexWrap: 'wrap',
  },
  sectionHeader: {
    fontSize: theme.pxToRem(15),
  },
  divider: {
    gridColumn: '1/-1',
    margin: 0,
  },
  dividerHeader: {
    backgroundColor: lighten(theme.palette.divider, 0.4),
    height: theme.pxToRem(3),
  },
  threeGrid: {
    display: 'grid',
    gap: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    gridTemplateColumns: 'repeat(3, 1fr)',
    marginTop: theme.spacing(1),
  },
  twoGrid: {
    display: 'grid',
    gap: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    gridTemplateColumns: 'repeat(2, 1fr)',
    marginTop: theme.spacing(1),
  },
  bioMarkerHeader: {
    color: theme.palette.text.secondary,
    gridColumn: 'span 2',
  },
  alignRight: {
    textAlign: 'right',
  },
  emptyBox: {
    '& svg': {
      height: theme.pxToRem(64),
      '& path': {
        strokeWidth: theme.pxToRem(1.5),
      },
    },
  },
}));

const potentialIHCFields = [
  {
    name: 'intensity',
    message: messages.ihcIntensity,
  },
  {
    name: 'stainPercent',
    message: messages.ihcStainPercent,
  },
  {
    name: 'threshold',
    message: messages.ihcThreshold,
  },
  {
    name: 'tcResult',
    message: messages.ihcTcResult,
  },
  {
    name: 'tcIntensity',
    message: messages.ihcTcIntensity,
  },
  {
    name: 'tcStainPercent',
    message: messages.ihcTcStainPercent,
  },
  {
    name: 'tcThreshold',
    message: messages.ihcTcThreshold,
  },
  {
    name: 'icResult',
    message: messages.ihcIcResult,
  },
  {
    name: 'icStainPercent',
    message: messages.ihcIcStainPercent,
  },
  {
    name: 'icThreshold',
    message: messages.ihcIcThreshold,
  },
  {
    name: 'cpScore',
    message: messages.ihcCpScore,
  },
] as const;

const notableBiomarkerMappings: Record<string, (value: any) => boolean> = {
  cellPurity: (cellPurity?: number) => !!cellPurity && cellPurity < 80,
  msi: (msi?: string) => msi?.toLowerCase() === 'msi-h',
  tmb: (tmb?: { status: string }) => tmb?.status?.toLowerCase() === 'high',
  ptf: (ptf?: string) => ptf?.toLowerCase() === 'elevated',
  hrd: (hrd?: string) => hrd?.toLowerCase() === 'positive',
  loh: (loh?: { status: string }) => loh?.status?.toLowerCase() === 'high',
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  nhc: (nhc?: { status: string }[]) =>
    nhc?.some(({ status }) => status?.toLowerCase() === 'likely'),
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  ihc: (ihc?: { result: string }[]) =>
    ihc?.some(({ result }) =>
      ['hr-negative her2-low', 'positive'].includes(result?.toLowerCase()),
    ),
};

export const doesTestHaveNotableBiomarkers = (test?: Test) =>
  Object.entries(notableBiomarkerMappings).some(([key, isNotable]) =>
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    isNotable(test?.[key]),
  );

const doesTestHaveTumorProfile = (test?: Test) =>
  !!(
    test?.msi ||
    test?.tmb ||
    test?.loh ||
    test?.ptf ||
    test?.hrd ||
    test?.cellPurity ||
    test?.hyperdiploidyTrisomies?.length
  );

export const doesTestHaveBiomarkers = (test?: Test) =>
  !!(
    doesTestHaveTumorProfile(test) ||
    test?.ihc?.length ||
    test?.nhc?.length ||
    test?.hla?.length
  );

interface BiomarkerSlideOverProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  activeTest?: Test;
}

const getHyperdiploidyMessage = (hyperdiploidyTrisomies: string[]) => {
  const formattedTrisomies = hyperdiploidyTrisomies.map(trisomy =>
    trisomy.replace('Chr', ''),
  );

  if (formattedTrisomies.length === 1) return `Chr ${formattedTrisomies[0]}`;
  if (formattedTrisomies.length === 2)
    return `Chr ${formattedTrisomies[0]} and ${formattedTrisomies[1]}`;
  return `Chr ${formattedTrisomies.slice(0, -1).join(', ')}, and ${
    formattedTrisomies[formattedTrisomies.length - 1]
  }`;
};

const BiomarkerSlideOver: React.FC<BiomarkerSlideOverProps> = ({
  isOpen,
  setIsOpen,
  activeTest,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const hasTumorProfile = doesTestHaveTumorProfile(activeTest);
  const hasAnyBiomarkers = doesTestHaveBiomarkers(activeTest);

  return (
    <SlideOver isOpen={isOpen}>
      <Header
        onClose={() => setIsOpen(false)}
        title={formatMessage(messages.biomarkers)}
        titleIcon={MapPin}
      />
      {activeTest && hasAnyBiomarkers ? (
        <Box direction="column" gap={3} padding={2}>
          {hasTumorProfile && (
            <Box direction="column">
              <Text className={classes.sectionHeader} weight="bold">
                {formatMessage(messages.tumorProfile)}
              </Text>
              <Box className={classes.tumorDetails} gap={1} marginTop={1}>
                {activeTest.msi && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testMSILabel)}
                        tooltipTitle={formatMessage(messages.tooltipMSI)}
                      />
                      <SequenceDetailsValue value={activeTest.msi} />
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.tmb && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testTMBLabel)}
                        tooltipTitle={formatMessage(messages.tooltipTMB)}
                      />
                      <SequenceDetailsValue value={activeTest?.tmb?.status} />
                      <SequenceDetailsBadge
                        value={activeTest?.tmb?.score?.toFixed(2)}
                      />
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.loh && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testLOHLabel)}
                        tooltipTitle={formatMessage(messages.tooltipLOH)}
                      />
                      <SequenceDetailsValue value={activeTest?.loh?.status} />
                      {activeTest.loh.score !== -1 && (
                        <SequenceDetailsBadge
                          value={formatMessage(messages.testLOHScore, {
                            lohScore: activeTest?.loh?.score?.toFixed(1),
                          })}
                        />
                      )}
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.ptf && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testPTFLabel)}
                        tooltipTitle={formatMessage(messages.tooltipPTF)}
                      />
                      <SequenceDetailsValue value={activeTest.ptf} />
                      {activeTest.ptfScore !== undefined && (
                        <SequenceDetailsBadge
                          value={`${activeTest.ptfScore.toFixed(1)}%`}
                        />
                      )}
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.hrd && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testHRDLabel)}
                        tooltipTitle={formatMessage(messages.tooltipHRD)}
                      />
                      <SequenceDetailsValue value={activeTest.hrd} />
                      {activeTest.hrdScore !== undefined && (
                        <SequenceDetailsBadge
                          value={`${activeTest.hrdScore.toFixed(2)}`}
                        />
                      )}
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.cellPurity && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.testCellPurityLabel)}
                        tooltipTitle={formatMessage(messages.tooltipCellPurity)}
                      />
                      <SequenceDetailsBadge
                        value={activeTest.cellPurity.toFixed(1).toString()}
                      />
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
                {activeTest.hyperdiploidyTrisomies?.length && (
                  <SequenceDetailsBox>
                    <SequenceDetailsPair>
                      <SequenceDetailsLabel
                        label={formatMessage(messages.hyperdiploidy)}
                        tooltipTitle={formatMessage(
                          messages.hyperdiploidyTooltip,
                        )}
                      />
                      <Box style={{ maxWidth: 270, overflowX: 'scroll' }}>
                        <SequenceDetailsValue
                          value={getHyperdiploidyMessage(
                            activeTest.hyperdiploidyTrisomies,
                          )}
                          preserveCase
                        />
                      </Box>
                    </SequenceDetailsPair>
                  </SequenceDetailsBox>
                )}
              </Box>
            </Box>
          )}
          {!!activeTest.nhc?.length && (
            <Box direction="column">
              <Text className={classes.sectionHeader} weight="bold">
                {formatMessage(messages.nonHumanContent)}
              </Text>
              <Box className={classes.threeGrid}>
                <Divider className={classes.divider} />
                {activeTest.nhc.map((nhc, index) => (
                  <React.Fragment key={index}>
                    <Text size="caption">
                      {nhc.organism.replace(' Non-human Content', '')}
                    </Text>
                    <Text className={classes.alignRight} size="caption">
                      {nhc.reads}
                    </Text>
                    <Text className={classes.alignRight} size="caption">
                      {nhc.status}
                    </Text>
                    <Divider className={classes.divider} />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}
          {!!activeTest.hla?.length && (
            <Box direction="column">
              <Text className={classes.sectionHeader} weight="bold">
                {formatMessage(messages.hla)}
              </Text>
              <Text
                className={classes.bioMarkerHeader}
                weight="bold"
                size="subbody"
              >
                {formatMessage(messages.mhcClassI)}
              </Text>
              <Box className={classes.twoGrid}>
                <Text size="caption" weight="bold">
                  {formatMessage(messages.gene)}
                </Text>
                <Text size="caption" weight="bold">
                  {formatMessage(messages.genotype)}
                </Text>
                <Divider
                  className={clsx(classes.divider, classes.dividerHeader)}
                />
                {activeTest.hla.map((hla, index) => (
                  <React.Fragment key={index}>
                    <Text size="caption">{hla.gene}</Text>
                    <Text size="caption">{hla.genotype}</Text>
                    <Divider className={classes.divider} />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}
          {!!activeTest.ihc?.length && (
            <Box direction="column">
              <Text className={classes.sectionHeader} weight="bold">
                {formatMessage(messages.immunohistochemistry)}
              </Text>
              {activeTest.ihc.map((ihc, index) => (
                <Box
                  className={classes.twoGrid}
                  direction="column"
                  key={index}
                  marginBottom={2}
                >
                  <Text
                    className={classes.bioMarkerHeader}
                    size="subbody"
                    weight="bold"
                  >
                    {ihc.biomarker.replace(' IHC Result', '')}
                  </Text>
                  <Divider
                    className={clsx(classes.divider, classes.dividerHeader)}
                  />
                  <Text size="caption">{formatMessage(messages.result)}</Text>
                  <Text size="caption">{ihc.result}</Text>
                  <Divider className={classes.divider} />
                  {potentialIHCFields.map(({ name, message }) => {
                    if (ihc[name]) {
                      return (
                        <>
                          <Text size="caption">{formatMessage(message)}</Text>
                          <Text size="caption">{ihc[name]}</Text>
                          <Divider className={classes.divider} />
                        </>
                      );
                    }
                  })}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          className={classes.emptyBox}
          align="center"
          direction="column"
          justify="center"
          fullHeight
          fullWidth
        >
          <EmptyPageState
            illustration={MapPin}
            message={formatMessage(messages.noBiomarkers)}
          />
        </Box>
      )}
    </SlideOver>
  );
};

export default BiomarkerSlideOver;
